import React, { useEffect, useMemo, useState } from "react";
import {
  Avatar,
  Box,
  Checkbox,
  Chip,
  MenuItem,
  Paper,
  Select,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import TaskTableRowActionsMenu from "./TaskTableRowActionsMenu";
import TaskTableEmptyState from "./TaskTableEmptyState";
import noTemplatesImage from "../../Images/Tasks/no-templates.svg";
import {
  DEFAULT_PRIORITY_OPTIONS,
  DEFAULT_TYPE_OPTIONS,
  mergeStatusOptionsWithDefaults,
  getFieldOptionChipSx,
  getFieldOptionValue,
  normalizeFieldOptions,
  taskTableInlineSelectSx,
  taskTableInlineSelectStatusSx,
} from "../../utils/taskTemplateChipStyles";
import {
  formatTemplateDeadlineOffsetLabel,
  renderTaskModifiedOnCell,
} from "../../utils/taskDeadlineDisplay";
import {
  TASK_TABLE_DEFAULT_ROWS_PER_PAGE,
  TASK_TABLE_ROWS_PER_PAGE_OPTIONS,
} from "../../utils/taskTablePagination";
import {
  taskTableContainerBorderedSx,
  taskTableGridTableSx,
  taskTableOuterBorder,
} from "../../utils/taskTableGridSx";
import {
  taskTableFooterShrinkSx,
  taskTableNaturalPaperSx,
} from "../../utils/taskTableScrollSx";
import {
  BULK_ACTIONS,
  TaskTableBulkActionsBar,
  buildTemplateBulkActionOptions,
} from "./taskTableBulkActions";
const PERSON_AVATAR_PALETTE = [
  "#6366f1", "#ec4899", "#f59e0b", "#10b981",
  "#3b82f6", "#8b5cf6", "#ef4444", "#14b8a6",
  "#f97316", "#06b6d4", "#84cc16", "#e11d48",
];

const personAvatarColor = (name) => {
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return PERSON_AVATAR_PALETTE[Math.abs(hash) % PERSON_AVATAR_PALETTE.length];
};

const renderPersonCell = (name) => {
  // Slice sends "—" as the "nobody assigned" placeholder — no avatar, just the dash.
  if (!name || name === "—") return "—";
  const initial = String(name).trim()[0]?.toLocaleUpperCase() || "?";
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.85, minWidth: 0 }}>
      <Avatar sx={{ width: 24, height: 24, bgcolor: personAvatarColor(name), fontSize: "0.6rem", fontWeight: 700, color: "#fff" }}>
        {initial}
      </Avatar>
      <Typography component="span" sx={{ fontSize: { xs: "0.88rem", sm: "0.95rem", md: "0.98rem" }, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", minWidth: 0 }}>
        {name}
      </Typography>
    </Box>
  );
};

/** UI-only: column stays in the DOM so status edit/filter logic is unchanged. */
const SHOW_STATUS_COLUMN = false;
const statusColumnHiddenSx = SHOW_STATUS_COLUMN ? undefined : { display: "none" };

const resolveFieldOptionsList = (fieldOptions, kind) => {
  const key = kind === "status" ? "statusOptions" : kind === "priority" ? "priorityOptions" : "typeOptions";
  if (kind === "status") {
    return mergeStatusOptionsWithDefaults(fieldOptions?.statusOptions || []);
  }
  const defaults =
    kind === "priority" ? DEFAULT_PRIORITY_OPTIONS : DEFAULT_TYPE_OPTIONS;
  const raw = fieldOptions?.[key]?.length ? fieldOptions[key] : defaults;
  return normalizeFieldOptions(raw, kind);
};

const renderColoredOptionChip = (value, options, kind) => {
  if (!value) return "—";
  return <Chip size="small" label={value} sx={getFieldOptionChipSx(value, options, kind)} />;
};

const checkboxCellSx = {
  py: 0.25,
  pl: 2,
  pr: 0,
  width: 44,
  minWidth: 44,
  maxWidth: 44,
  boxSizing: "border-box",
  overflow: "hidden",
  "&.MuiTableCell-paddingCheckbox": {
    pl: 2,
    pr: 0,
    width: 44,
    minWidth: 44,
    maxWidth: 44,
  },
};

const rowMenuCellSx = {
  width: 28,
  pl: 0,
  pr: 0.5,
  textAlign: "left",
};

const nameHeaderCellSx = {
  width: { xs: "17%", md: "20%" },
  minWidth: { xs: 140, md: 180 },
  pl: 0.75,
};

const deadlineHeaderCellSx = {
  width: { xs: "11%", md: "12%" },
};

const assigneeHeaderCellSx = {
  width: { xs: "13%", md: "14%" },
};

const createdByHeaderCellSx = {
  width: { xs: "12%", md: "13%" },
};

const nameBodyCellSx = {
  py: 1.3,
  pl: 0.75,
  fontSize: { xs: "0.88rem", sm: "0.95rem", md: "0.98rem" },
  whiteSpace: "normal",
  overflowWrap: "anywhere",
  wordBreak: "break-word",
  lineHeight: 1.35,
};

const textBodyCellSx = {
  py: 1.3,
  fontSize: { xs: "0.88rem", sm: "0.95rem", md: "0.98rem" },
  whiteSpace: "normal",
  overflowWrap: "anywhere",
  wordBreak: "break-word",
  lineHeight: 1.35,
};

const TaskTemplatesTable = ({
  rows,
  fieldOptions,
  loading = false,
  onView,
  onEdit,
  onDelete,
  onBulkDelete,
  onStatusChange,
  onPriorityChange,
  onTypeChange,
  canView = true,
  canEdit = true,
  canEditRow = null,
  /** Server-side pagination state — `rows` is already the current page. */
  pagination = { page: 1, limit: TASK_TABLE_DEFAULT_ROWS_PER_PAGE, total: 0 },
  onPageChange,
  onRowsPerPageChange,
}) => {
  const resolveCanEdit = (row) => (typeof canEditRow === "function" ? canEditRow(row) : canEdit);
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkAction, setBulkAction] = useState("");

  const typeOptions = useMemo(() => resolveFieldOptionsList(fieldOptions, "type"), [fieldOptions]);
  const statusOptions = useMemo(() => resolveFieldOptionsList(fieldOptions, "status"), [fieldOptions]);

  const paginatedRows = rows;

  const selectedRows = useMemo(
    () => rows.filter((row) => selectedIds.includes(row.id)),
    [rows, selectedIds]
  );

  const hasSelection = selectedRows.length > 0;

  const bulkActionOptions = useMemo(
    () =>
      buildTemplateBulkActionOptions(selectedRows, {
        canView,
        canEdit,
        canEditRow: typeof canEditRow === "function" ? canEditRow : null,
      }),
    [selectedRows, canView, canEdit, canEditRow]
  );

  useEffect(() => {
    if (!hasSelection) {
      setBulkAction("");
      return;
    }
    if (bulkAction && !bulkActionOptions.some((opt) => opt.value === bulkAction)) {
      setBulkAction("");
    }
  }, [bulkAction, bulkActionOptions, hasSelection]);

  const canApplyBulkAction = hasSelection && bulkAction && bulkActionOptions.some((o) => o.value === bulkAction);

  const allVisibleSelected =
    paginatedRows.length > 0 && paginatedRows.every((row) => selectedIds.includes(row.id));

  const someVisibleSelected =
    paginatedRows.some((row) => selectedIds.includes(row.id)) && !allVisibleSelected;

  const handleSelectAll = (checked) => {
    const visibleIds = paginatedRows.map((row) => row.id);
    if (checked) {
      setSelectedIds((prev) => Array.from(new Set([...prev, ...visibleIds])));
    } else {
      setSelectedIds((prev) => prev.filter((id) => !visibleIds.includes(id)));
    }
  };

  const handleRowSelect = (id) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const handleChangePage = (_, newPage0) => onPageChange?.(newPage0 + 1);

  const handleChangeRowsPerPage = (event) => {
    onRowsPerPageChange?.(parseInt(event.target.value, 10));
  };

  const handleApplyBulkAction = () => {
    if (!hasSelection || !bulkAction) return;

    if (bulkAction === BULK_ACTIONS.VIEW && selectedRows.length === 1) {
      onView?.(selectedRows[0]);
      setBulkAction("");
      return;
    }
    if (bulkAction === BULK_ACTIONS.EDIT && selectedRows.length === 1) {
      onEdit?.(selectedRows[0]);
      setBulkAction("");
      return;
    }
    const deletableRows = selectedRows.filter((row) => resolveCanEdit(row));
    if (bulkAction === BULK_ACTIONS.DELETE && deletableRows.length > 0) {
      if (onBulkDelete) {
        onBulkDelete(deletableRows);
      } else {
        deletableRows.forEach((row) => onDelete?.(row));
      }
      setSelectedIds([]);
      setBulkAction("");
    }
  };

  const tableColSpan = 9;

  // Row hamburger menu items (left of the template name). Extend this array to
  // add future per-row actions — the menu renders whatever is passed in.
  const buildRowActions = (row) => {
    const rowCanEdit = resolveCanEdit(row);
    return [
      {
        key: "edit",
        label: "Edit",
        icon: <EditIcon sx={{ fontSize: "1.05rem" }} />,
        show: rowCanEdit,
        onClick: (r) => onEdit?.(r),
      },
      {
        key: "delete",
        label: "Delete",
        icon: <DeleteIcon sx={{ fontSize: "1.05rem" }} />,
        color: "error.main",
        show: rowCanEdit,
        onClick: (r) => onDelete?.(r),
      },
    ];
  };

  return (
    <Paper
      elevation={0}
      sx={{
        ...taskTableNaturalPaperSx,
        backgroundColor: (theme) => theme.palette.background.paper,
        overflow: "hidden",
        boxSizing: "border-box",
        borderRadius: 2,
        border: (theme) => taskTableOuterBorder(theme),
      }}
    >
      <TableContainer sx={taskTableContainerBorderedSx}>
        <Table
          stickyHeader
          aria-label="task templates table"
          sx={{ ...taskTableGridTableSx(true), tableLayout: "fixed" }}
        >
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox" sx={checkboxCellSx}>
                <Checkbox
                  size="small"
                  checked={allVisibleSelected}
                  indeterminate={someVisibleSelected}
                  onChange={(event) => handleSelectAll(event.target.checked)}
                  sx={{ p: 0.35 }}
                />
              </TableCell>
              <TableCell sx={rowMenuCellSx} aria-label="Actions" />
              <TableCell sx={nameHeaderCellSx}>
                <Typography fontWeight={700} sx={{ fontSize: { xs: "0.9rem", sm: "0.98rem", md: "1rem" } }}>Name</Typography>
              </TableCell>
              <TableCell sx={deadlineHeaderCellSx}>
                <Typography fontWeight={700} sx={{ fontSize: { xs: "0.9rem", sm: "0.98rem", md: "1rem" } }}>Deadline</Typography>
              </TableCell>
              <TableCell sx={createdByHeaderCellSx}>
                <Typography fontWeight={700} sx={{ fontSize: { xs: "0.9rem", sm: "0.98rem", md: "1rem" } }}>Created By</Typography>
              </TableCell>
              <TableCell sx={assigneeHeaderCellSx}>
                <Typography fontWeight={700} sx={{ fontSize: { xs: "0.9rem", sm: "0.98rem", md: "1rem" } }}>Assignee</Typography>
              </TableCell>
              <TableCell sx={statusColumnHiddenSx}>
                <Typography fontWeight={700} sx={{ fontSize: { xs: "0.9rem", sm: "0.98rem", md: "1rem" } }}>Status</Typography>
              </TableCell>
              <TableCell>
                <Typography fontWeight={700} sx={{ fontSize: { xs: "0.9rem", sm: "0.98rem", md: "1rem" } }}>Type</Typography>
              </TableCell>
              <TableCell>
                <Typography fontWeight={700} sx={{ fontSize: { xs: "0.9rem", sm: "0.98rem", md: "1rem" } }}>Modified On</Typography>
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {loading ? (
              Array.from({ length: 6 }).map((_, i) => (
                <TableRow key={`sk-${i}`}>
                  <TableCell padding="checkbox" sx={checkboxCellSx}>
                    <Skeleton variant="rectangular" width={18} height={18} sx={{ borderRadius: 0.5 }} />
                  </TableCell>
                  <TableCell sx={rowMenuCellSx} />
                  <TableCell sx={nameBodyCellSx}>
                    <Skeleton width="70%" />
                  </TableCell>
                  <TableCell sx={textBodyCellSx}>
                    <Skeleton width="60%" />
                  </TableCell>
                  <TableCell sx={{ ...textBodyCellSx, ...createdByHeaderCellSx }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.85 }}>
                      <Skeleton variant="circular" width={24} height={24} />
                      <Skeleton width="55%" />
                    </Box>
                  </TableCell>
                  <TableCell sx={textBodyCellSx}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.85 }}>
                      <Skeleton variant="circular" width={24} height={24} />
                      <Skeleton width="55%" />
                    </Box>
                  </TableCell>
                  <TableCell sx={{ py: 1.3, ...statusColumnHiddenSx }} />
                  <TableCell sx={{ py: 1.3 }}>
                    <Skeleton variant="rounded" width={72} height={24} sx={{ borderRadius: 999 }} />
                  </TableCell>
                  <TableCell>
                    <Skeleton width="65%" />
                  </TableCell>
                </TableRow>
              ))
            ) : paginatedRows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={tableColSpan} align="center">
                  <TaskTableEmptyState
                    image={noTemplatesImage}
                    icon={ArticleOutlinedIcon}
                    color="#6366f1"
                    title="No templates found"
                    subtitle="Create a template to speed up future task creation."
                  />
                </TableCell>
              </TableRow>
            ) : (
              paginatedRows.map((row) => {
                const rowCanEdit = resolveCanEdit(row);
                return (
                <TableRow key={row.id} hover>
                  <TableCell padding="checkbox" sx={checkboxCellSx}>
                    <Checkbox
                      size="small"
                      checked={selectedIds.includes(row.id)}
                      onChange={() => handleRowSelect(row.id)}
                      sx={{ p: 0.2, m: 0 }}
                    />
                  </TableCell>
                  <TableCell sx={rowMenuCellSx} onClick={(e) => e.stopPropagation()}>
                    <TaskTableRowActionsMenu row={row} actions={buildRowActions(row)} />
                  </TableCell>
                  <TableCell
                    sx={{
                      ...nameBodyCellSx,
                      color: "text.primary",
                      cursor: canView ? "pointer" : "default",
                      ...(canView
                        ? {
                            "& .task-name-text": {
                              transition: "color 0.15s ease",
                            },
                            "&:hover .task-name-text": {
                              color: "primary.main",
                              textDecoration: "underline",
                              textUnderlineOffset: "3px",
                              textDecorationThickness: "1px",
                            },
                          }
                        : {}),
                    }}
                    onClick={() => canView && onView?.(row)}
                  >
                    <Box component="span" className="task-name-text">
                      {row.name}
                    </Box>
                  </TableCell>
                  <TableCell sx={textBodyCellSx}>
                    {formatTemplateDeadlineOffsetLabel(row.deadlineOffset)}
                  </TableCell>
                  <TableCell sx={{ ...textBodyCellSx, ...createdByHeaderCellSx }}>
                    {renderPersonCell(row.createdBy)}
                  </TableCell>
                  <TableCell sx={textBodyCellSx}>
                    {renderPersonCell(row.assigne)}
                  </TableCell>
                  <TableCell sx={{ py: 1.3, ...statusColumnHiddenSx }} onClick={(e) => e.stopPropagation()}>
                    {rowCanEdit ? (
                      <Select
                        variant="standard"
                        disableUnderline
                        size="small"
                        value={row.status || ""}
                        onChange={(e) => onStatusChange?.(row, e.target.value)}
                        displayEmpty
                        renderValue={(selected) => renderColoredOptionChip(selected, statusOptions, "status")}
                        sx={taskTableInlineSelectStatusSx}
                      >
                        <MenuItem value="">—</MenuItem>
                        {statusOptions.map((opt) => {
                          const val = getFieldOptionValue(opt);
                          return (
                            <MenuItem key={val} value={val}>
                              {renderColoredOptionChip(val, statusOptions, "status")}
                            </MenuItem>
                          );
                        })}
                      </Select>
                    ) : row.status ? (
                      <Chip
                        size="small"
                        label={row.status}
                        sx={getFieldOptionChipSx(row.status, statusOptions, "status")}
                      />
                    ) : (
                      "—"
                    )}
                  </TableCell>
                  <TableCell sx={{ py: 1.3 }} onClick={(e) => e.stopPropagation()}>
                    {rowCanEdit ? (
                      <Select
                        variant="standard"
                        disableUnderline
                        size="small"
                        value={row.templateType || ""}
                        onChange={(e) => onTypeChange?.(row, e.target.value)}
                        displayEmpty
                        renderValue={(selected) => renderColoredOptionChip(selected, typeOptions, "type")}
                        sx={taskTableInlineSelectSx}
                      >
                        <MenuItem value="">—</MenuItem>
                        {typeOptions.map((opt) => {
                          const val = getFieldOptionValue(opt);
                          return (
                            <MenuItem key={val} value={val}>
                              {renderColoredOptionChip(val, typeOptions, "type")}
                            </MenuItem>
                          );
                        })}
                      </Select>
                    ) : row.templateType ? (
                      <Chip
                        size="small"
                        label={row.templateType}
                        sx={getFieldOptionChipSx(row.templateType, typeOptions, "type")}
                      />
                    ) : (
                      "—"
                    )}
                  </TableCell>
                  <TableCell sx={textBodyCellSx}>
                    {renderTaskModifiedOnCell(row)}
                  </TableCell>
                </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={taskTableFooterShrinkSx}>
      <TaskTableBulkActionsBar
        totalCount={rows.length}
        selectedCount={selectedIds.length}
        bulkAction={bulkAction}
        onBulkActionChange={setBulkAction}
        bulkActionOptions={bulkActionOptions}
        onApply={handleApplyBulkAction}
        canApply={canApplyBulkAction}
        hasSelection={hasSelection}
        bordered
        bulkBarInset
        borderTop={(theme) => taskTableOuterBorder(theme)}
        pagination={{
          count: pagination.total,
          page: pagination.page - 1,
          onPageChange: handleChangePage,
          rowsPerPage: pagination.limit,
          onRowsPerPageChange: handleChangeRowsPerPage,
          rowsPerPageOptions: TASK_TABLE_ROWS_PER_PAGE_OPTIONS,
          sx: {
            "& .MuiTablePagination-toolbar": {
              minHeight: { xs: 40, sm: 44 },
              px: 0,
              flexWrap: "nowrap",
            },
            "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
              fontSize: { xs: "0.84rem", sm: "0.92rem", md: "0.96rem" },
            },
            "& .MuiTablePagination-select": {
              fontSize: { xs: "0.84rem", sm: "0.92rem", md: "0.96rem" },
            },
            "& .MuiIconButton-root": { p: 0.4 },
          },
        }}
      />
      </Box>
    </Paper>
  );
};

export default TaskTemplatesTable;
